import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Building2, User, LogOut, Home, Inbox, CalendarClock, Users, MessageSquare, Star, Wallet, Tag, Wallet2, ListChecks, Receipt, Gauge, ListPlus, ChevronDown, ClipboardList, Globe, CreditCard, Lock, ShieldCheck, AlertTriangle } from "lucide-react";
import apiClient from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const navItems = [
  { to: "/owner", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "owner-nav-dashboard" },
  { to: "/owner/properties", label: "Property Saya", icon: Building2, testid: "owner-nav-properties" },
  { to: "/owner/minisite", label: "Web-Site", icon: Globe, testid: "owner-nav-minisite" },
  { to: "/owner/bookings", label: "Booking Masuk", icon: Inbox, testid: "owner-nav-bookings" },
  {
    group: "report", label: "Report", icon: ClipboardList, testid: "owner-nav-report-group",
    children: [
      { to: "/owner/surveys", label: "Survei", icon: CalendarClock, testid: "owner-nav-surveys" },
      { to: "/owner/tenants", label: "Penghuni", icon: Users, testid: "owner-nav-tenants" },
      { to: "/owner/waitlist", label: "Waiting List", icon: ListChecks, testid: "owner-nav-waitlist" },
      { to: "/owner/finance", label: "Keuangan", icon: Wallet2, testid: "owner-nav-finance" },
    ],
  },
  {
    group: "billing", label: "Billing", icon: Receipt, testid: "owner-nav-billing-group",
    children: [
      { to: "/owner/billing", label: "Daftar Billing", icon: Receipt, end: true, testid: "owner-nav-billing" },
      { to: "/owner/meters", label: "Meteran", icon: Gauge, testid: "owner-nav-meters" },
      { to: "/owner/billing/components", label: "Komponen Biaya", icon: ListPlus, testid: "owner-nav-billing-components" },
      { to: "/owner/refunds", label: "Refund", icon: Wallet, testid: "owner-nav-refunds" },
    ],
  },
  { to: "/owner/promos", label: "Promo", icon: Tag, testid: "owner-nav-promos" },
  { to: "/owner/reviews", label: "Ulasan", icon: Star, testid: "owner-nav-reviews" },
  { to: "/owner/messages", label: "Pesan", icon: MessageSquare, testid: "owner-nav-messages", badge: true },
  { to: "/owner/subscription", label: "Subscription", icon: ShieldCheck, testid: "owner-nav-subscription" },
  { to: "/owner/payment-settings", label: "Payment Settings", icon: CreditCard, testid: "owner-nav-payment-settings" },
  { to: "/owner/profile", label: "Profile", icon: User, testid: "owner-nav-profile" },
  { to: "/pengaturan-akun", label: "Keamanan Akun", icon: Lock, testid: "owner-nav-security" },
];

function NavGroup({ item, pathname, locked = false }) {
  const childActive = item.children.some((c) => pathname === c.to || pathname.startsWith(c.to + "/"));
  const [open, setOpen] = useState(childActive);
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={item.testid}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
          childActive ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-secondary/60"
        }`}
      >
        <item.icon size={16} /> <span className="flex-1 text-left">{item.label}</span>
        {locked && <Lock size={13} className="text-rose-400" />}
        <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1 ml-3 pl-3 border-l border-border space-y-1">
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={locked ? "/owner/subscription" : c.to}
              end={c.end}
              data-testid={c.testid}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                  isActive && !locked ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-secondary/60"
                }`
              }
            >
              <c.icon size={15} /> <span className="flex-1">{c.label}</span>
              {locked && <Lock size={12} className="text-rose-400" />}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OwnerLayout({ children, testid = "owner-shell" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    const load = () => apiClient.get("/conversations/unread-count").then((r) => setUnread(r.data?.unread || 0)).catch(() => {});
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    apiClient.get("/subscription/me").then((r) => setSub(r.data)).catch(() => {});
  }, [location.pathname]);

  const gated = sub?.gated;
  const LOCKED_PATHS = ["/owner/properties", "/owner/minisite", "/owner/bookings"];
  const isLocked = (to) => gated && LOCKED_PATHS.includes(to);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid={testid}>
      <div className="flex flex-col lg:flex-row gap-8">
        <aside className="lg:w-64 shrink-0">
          <div className="bg-card border border-border rounded-2xl p-4 lg:sticky lg:top-24">
            <div className="flex items-center gap-2 px-2 pb-4 mb-2 border-b border-border">
              <span className="grid place-items-center w-8 h-8 rounded-lg bg-primary text-primary-foreground">
                <Home size={16} />
              </span>
              <div className="min-w-0">
                <p className="font-heading text-sm font-semibold leading-tight truncate">{user?.name || "Owner"}</p>
                <p className="text-xs text-muted-foreground">Owner Dashboard</p>
              </div>
            </div>
            <nav className="space-y-1">
              {navItems.map((item) => (
                item.children ? (
                  <NavGroup key={item.group} item={item} pathname={location.pathname} locked={gated && item.group === "billing"} />
                ) : (
                <NavLink
                  key={item.to}
                  to={isLocked(item.to) ? "/owner/subscription" : item.to}
                  end={item.end}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-secondary/60"
                    }`
                  }
                >
                  <item.icon size={16} /> <span className="flex-1">{item.label}</span>
                  {isLocked(item.to) && <Lock size={13} className="text-rose-400" />}
                  {item.badge && unread > 0 && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent text-accent-foreground" data-testid="owner-messages-badge">{unread}</span>
                  )}
                </NavLink>
                )
              ))}
              <button
                onClick={handleLogout}
                data-testid="owner-nav-logout"
                className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary/60 transition-colors"
              >
                <LogOut size={16} /> Logout
              </button>
            </nav>
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          {(gated || (sub?.warn != null)) && (
            <div className={`mb-5 flex items-start gap-2 rounded-xl border p-3 text-sm ${gated ? "bg-rose-50 border-rose-200 text-rose-800" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid="owner-subscription-banner">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span className="flex-1">
                {gated
                  ? "Subscription Anda telah berakhir. Properti & web-site Anda sementara dinonaktifkan."
                  : `Subscription Anda akan berakhir dalam ${sub.days_left} hari. Perpanjang sekarang agar properti tetap aktif.`}
              </span>
              <button onClick={() => navigate("/owner/subscription")} className="shrink-0 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold" data-testid="owner-banner-cta">
                {gated ? "Aktifkan" : "Perpanjang"}
              </button>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
